import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";
import { toast } from "sonner";

const PO_STATUSES = ["Draft", "Sent", "Accepted", "Partially Delivered", "Fully Delivered", "Cancelled", "Closed"];

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, v, pr, br, pj] = await Promise.all([
      api.get("/purchasing/purchase-orders"),
      api.get("/purchasing/vendors"),
      api.get("/purchasing/products"),
      api.get("/purchasing/brands"),
      api.get("/projects/options").catch(() => ({ data: [] })),
    ]);
    setPos(p.data); setVendors(v.data); setProducts(pr.data); setBrands(br.data); setProjects(pj.data || []);
  };
  useEffect(() => { load(); }, []);

  const vendorName = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.name])), [vendors]);
  const projectName = useMemo(() => Object.fromEntries((projects || []).map((p) => [p.id, p.customer_name])), [projects]);

  const rows = statusFilter === "all" ? pos : pos.filter((p) => p.status === statusFilter);

  const setStatus = async (p, status) => {
    try { await api.patch(`/purchasing/purchase-orders/${p.id}`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete PO ${p.po_number}?`)) return;
    try { await api.delete(`/purchasing/purchase-orders/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="pv-po-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Purchase Orders"
        subtitle="Line items with CGST/SGST/IGST split. Track status from Draft to Closed."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="pv-new-po-btn">
                <Plus className="w-4 h-4 mr-2" /> New PO
              </Button>
            </DialogTrigger>
            {open && <PODialog editing={editing} vendors={vendors} products={products} brands={brands} projects={projects}
              onDone={() => { setOpen(false); setEditing(null); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <div className="mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-none h-10 w-56" data-testid="pv-po-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["PO #", "Date", "Vendor", "Project", "Items", "Total", "Status", ""].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-po-row-${p.id}`}>
                  <td className="px-4 py-3 text-xs font-mono font-bold">{p.po_number}</td>
                  <td className="px-4 py-3 text-xs">{p.po_date}</td>
                  <td className="px-4 py-3 text-xs font-semibold">{vendorName[p.vendor_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs">{projectName[p.project_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs">{(p.items || []).length} line{(p.items || []).length === 1 ? "" : "s"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{inr(p.grand_total)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Select value={p.status} onValueChange={(v) => setStatus(p, v)}>
                      <SelectTrigger className="rounded-none h-8 text-xs w-44" data-testid={`pv-po-status-${p.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setEditing(p); setOpen(true); }} className="text-[#4B5563] hover:text-[#002FA7] mr-3" data-testid={`pv-po-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`pv-po-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#9CA3AF] py-12">No purchase orders.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PODialog({ editing, vendors, products, brands, projects, onDone }) {
  const isEdit = !!editing;
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    vendor_id: editing?.vendor_id || vendors[0]?.id || "",
    project_id: editing?.project_id || "",
    po_date: editing?.po_date || today,
    expected_delivery_date: editing?.expected_delivery_date || "",
    payment_terms: editing?.payment_terms || "",
    delivery_address: editing?.delivery_address || "",
    other_charges: editing?.other_charges ?? 0,
    remarks: editing?.remarks || "",
    status: editing?.status || "Draft",
    items: editing?.items?.length ? editing.items : [emptyItem(products[0]?.id)],
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const setItem = (i, k, v) => {
    const items = [...f.items]; items[i] = { ...items[i], [k]: v }; setF({ ...f, items });
  };
  const addItem = () => setF({ ...f, items: [...f.items, emptyItem(products[0]?.id)] });
  const rmItem = (i) => setF({ ...f, items: f.items.filter((_, x) => x !== i) });

  // Live-computed totals so user sees GST breakdown as they type.
  const totals = useMemo(() => {
    let sub = 0, cg = 0, sg = 0, ig = 0;
    for (const it of f.items) {
      const ls = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      sub += ls;
      cg += ls * (Number(it.cgst_pct) || 0) / 100;
      sg += ls * (Number(it.sgst_pct) || 0) / 100;
      ig += ls * (Number(it.igst_pct) || 0) / 100;
    }
    const oc = Number(f.other_charges) || 0;
    return { sub, cg, sg, ig, oc, grand: sub + cg + sg + ig + oc };
  }, [f.items, f.other_charges]);

  const submit = async () => {
    if (!f.vendor_id) { toast.error("Pick a vendor"); return; }
    if (!f.items.length) { toast.error("Add at least one item"); return; }
    for (const it of f.items) {
      if (!it.product_id) { toast.error("Every line needs a product"); return; }
      if (!it.quantity || Number(it.quantity) <= 0) { toast.error("Every line needs a quantity"); return; }
    }
    const payload = {
      ...f,
      other_charges: Number(f.other_charges) || 0,
      project_id: f.project_id || null,
      items: f.items.map((it) => ({
        product_id: it.product_id,
        brand_id: it.brand_id || null,
        description: it.description || null,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
        cgst_pct: Number(it.cgst_pct) || 0,
        sgst_pct: Number(it.sgst_pct) || 0,
        igst_pct: Number(it.igst_pct) || 0,
      })),
    };
    try {
      if (isEdit) await api.patch(`/purchasing/purchase-orders/${editing.id}`, payload);
      else await api.post("/purchasing/purchase-orders", payload);
      toast.success(isEdit ? "PO updated" : "PO created"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-4xl max-h-[92vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">{isEdit ? `Edit ${editing.po_number}` : "New Purchase Order"}</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="label-eyebrow">Vendor *</Label>
            <Select value={f.vendor_id} onValueChange={(v) => setF({ ...f, vendor_id: v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="pv-po-vendor"><SelectValue /></SelectTrigger>
              <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="label-eyebrow">Project (optional)</Label>
            <Select value={f.project_id || "_none"} onValueChange={(v) => setF({ ...f, project_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="pv-po-project"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— none —</SelectItem>
                {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.customer_name} · {p.qr_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="label-eyebrow">PO Date</Label><Input type="date" value={f.po_date} onChange={set("po_date")} className="rounded-none mt-2 h-10" data-testid="pv-po-date" /></div>
          <div><Label className="label-eyebrow">Expected Delivery</Label><Input type="date" value={f.expected_delivery_date} onChange={set("expected_delivery_date")} className="rounded-none mt-2 h-10" /></div>
          <div><Label className="label-eyebrow">Payment Terms</Label><Input value={f.payment_terms} onChange={set("payment_terms")} className="rounded-none mt-2 h-10" /></div>
        </div>
        <div><Label className="label-eyebrow">Delivery Address</Label><Input value={f.delivery_address} onChange={set("delivery_address")} className="rounded-none mt-2 h-10" /></div>

        <div className="border border-[#E5E7EB]">
          <div className="bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB] flex items-center justify-between">
            <div className="label-eyebrow">Line Items</div>
            <button onClick={addItem} className="text-xs uppercase tracking-widest font-bold text-[#002FA7] hover:underline" data-testid="pv-po-add-item">+ Add Item</button>
          </div>
          <div className="divide-y divide-[#F3F4F6]">
            {f.items.map((it, i) => {
              const brandsForProd = brands.filter((b) => b.product_id === it.product_id);
              return (
                <div key={i} className="p-3 grid grid-cols-12 gap-2 items-end" data-testid={`pv-po-item-${i}`}>
                  <div className="col-span-3">
                    <Label className="label-eyebrow !text-[9px]">Product</Label>
                    <SearchablePicker
                      items={products}
                      value={it.product_id}
                      onChange={(v) => setItem(i, "product_id", v)}
                      placeholder="Search product…"
                      testid={`pv-po-item-product-${i}`}
                      selectedLabel={(p) => p.name}
                      matchItem={productMatch}
                      renderRow={renderProductRow}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="label-eyebrow !text-[9px]">Brand</Label>
                    <Select value={it.brand_id || "_n"} onValueChange={(v) => setItem(i, "brand_id", v === "_n" ? "" : v)}>
                      <SelectTrigger className="rounded-none h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_n">—</SelectItem>
                        {brandsForProd.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1"><Label className="label-eyebrow !text-[9px]">Qty</Label><Input type="number" step="0.01" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="rounded-none h-9 text-xs" data-testid={`pv-po-item-qty-${i}`} /></div>
                  <div className="col-span-2"><Label className="label-eyebrow !text-[9px]">Unit Price ₹</Label><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="rounded-none h-9 text-xs" data-testid={`pv-po-item-price-${i}`} /></div>
                  <div className="col-span-1"><Label className="label-eyebrow !text-[9px]">CGST %</Label><Input type="number" step="0.01" value={it.cgst_pct} onChange={(e) => setItem(i, "cgst_pct", e.target.value)} className="rounded-none h-9 text-xs" /></div>
                  <div className="col-span-1"><Label className="label-eyebrow !text-[9px]">SGST %</Label><Input type="number" step="0.01" value={it.sgst_pct} onChange={(e) => setItem(i, "sgst_pct", e.target.value)} className="rounded-none h-9 text-xs" /></div>
                  <div className="col-span-1"><Label className="label-eyebrow !text-[9px]">IGST %</Label><Input type="number" step="0.01" value={it.igst_pct} onChange={(e) => setItem(i, "igst_pct", e.target.value)} className="rounded-none h-9 text-xs" /></div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => rmItem(i)} className="text-[#9CA3AF] hover:text-[#DC2626] p-1" title="Remove line" data-testid={`pv-po-item-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="bg-[#FAFAFA] px-4 py-3 border-t border-[#E5E7EB]">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs font-mono">
              <Sum label="Subtotal" v={totals.sub} />
              <Sum label="CGST" v={totals.cg} />
              <Sum label="SGST" v={totals.sg} />
              <Sum label="IGST" v={totals.ig} />
              <div>
                <div className="label-eyebrow !text-[9px] !text-[#4B5563]">Other Charges</div>
                <Input type="number" step="0.01" value={f.other_charges} onChange={set("other_charges")} className="rounded-none h-8 text-xs mt-1 font-mono" data-testid="pv-po-other-charges" />
              </div>
              <Sum label="Grand Total" v={totals.grand} big />
            </div>
          </div>
        </div>

        <div><Label className="label-eyebrow">Remarks</Label><Textarea value={f.remarks} onChange={set("remarks")} rows={2} className="rounded-none mt-2" /></div>
        <div className="grid grid-cols-2 gap-2 items-end">
          <div><Label className="label-eyebrow">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="pv-po-status"><SelectValue /></SelectTrigger>
              <SelectContent>{PO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={submit} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="pv-po-submit">
            {isEdit ? "Save Changes" : "Create Purchase Order"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

function emptyItem(productId) {
  return { product_id: productId || "", brand_id: "", description: "", quantity: 1, unit_price: 0, cgst_pct: 9, sgst_pct: 9, igst_pct: 0 };
}
function Sum({ label, v, big }) {
  return (
    <div>
      <div className="label-eyebrow !text-[9px] !text-[#4B5563]">{label}</div>
      <div className={`font-mono mt-1 ${big ? "text-base font-black text-[#0a0a0a]" : "text-sm"}`}>{inr(v)}</div>
    </div>
  );
}
